import { currentUserId, suiteRoot } from "./auth";

type WirePoint = { k: "plain" | "zoned"; v: string };
type WireEvent = {
  id?: string;
  title?: string;
  calendarId?: string;
  start?: WirePoint;
  end?: WirePoint;
  shareToKanban?: boolean;
};
type WireCalendar = { id?: string; isReadOnly?: boolean; name?: string; colorHex?: string | null };
type AgendaPack = {
  success?: boolean;
  message?: string;
  calendars?: WireCalendar[];
  events?: WireEvent[];
  prefs?: Record<string, unknown>;
};

function personal(data: AgendaPack): WireCalendar[] {
  const calendars = Array.isArray(data.calendars) ? data.calendars : [];
  const owned = calendars.filter((item) => item.isReadOnly !== true && item.id && !isConges(String(item.id)));
  const preferred = String(data.prefs?.defaultInviteCalendarId || "");
  return owned.sort((a, b) => (a.id === preferred ? -1 : b.id === preferred ? 1 : 0));
}

function isConges(id: string): boolean {
  return id === "conges" || /^conges_\d+$/.test(id);
}

async function agenda(method: "GET" | "POST", body?: unknown): Promise<AgendaPack> {
  const headers = new Headers();
  const userId = currentUserId();
  if (userId) headers.set("X-User-Id", userId);
  if (body) headers.set("Content-Type", "application/json");
  const res = await fetch(`${suiteRoot()}/api_suite/agenda-data.php`, {
    method,
    credentials: "include",
    cache: "no-store",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as AgendaPack;
  if (!res.ok || data.success === false) {
    throw new Error(data.message || "Agenda ne répond pas.");
  }
  return data;
}

export type AgendaChoice = { id: string; name: string };

export type AgendaMark = { id: string; title: string; day: string; time: string; color: string };

export async function listAllEvents(): Promise<AgendaMark[]> {
  const data = await agenda("GET");
  const colors = new Map<string, string>();
  for (const item of data.calendars || []) {
    if (item.id) colors.set(String(item.id), agendaColor(item.colorHex));
  }
  return (data.events || []).flatMap((item) => {
    const day = dayKey(item.start?.v);
    if (!day) return [];
    return [{
      id: String(item.id || day + item.title),
      title: item.title || "Rendez-vous",
      day,
      time: timeKey(item.start?.v),
      color: colors.get(String(item.calendarId || "")) || "#6366f1",
    }];
  });
}

function agendaColor(value?: string | null): string {
  const raw = String(value || "").trim().replace(/^#/, "");
  const body = raw.length >= 6 ? raw.slice(0, 6) : "";
  return /^[0-9a-fA-F]{6}$/.test(body) ? `#${body}` : "#6366f1";
}

function dayKey(value?: string): string {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

function timeKey(value?: string): string {
  const match = String(value || "").match(/T(\d{2}:\d{2})/);
  return match ? match[1] : "";
}

export async function listAgendas(): Promise<AgendaChoice[]> {
  const data = await agenda("GET");
  return personal(data).map((item) => ({ id: String(item.id), name: item.name || "Agenda" }));
}

export async function placeNote(input: {
  title: string;
  place: "agenda" | "kanban";
  day: string;
  time: string;
  calendarId: string;
}): Promise<void> {
  const data = await agenda("GET");
  const calendars = Array.isArray(data.calendars) ? data.calendars : [];
  const events = Array.isArray(data.events) ? data.events : [];
  const calendar = personal(data).find((item) => item.id === input.calendarId);
  if (!calendar?.id) throw new Error("Choisis un agenda.");

  const share = input.place === "kanban";
  const span = input.place === "agenda" ? timed(input.day, input.time) : allday(input.day);
  const next: WireEvent = {
    id: `crm-${Date.now().toString(36)}`,
    title: input.title.slice(0, 180) || "Note",
    calendarId: calendar.id,
    start: span.start,
    end: span.end,
    ...(share ? { shareToKanban: true } : {}),
  };

  await agenda("POST", {
    version: 1,
    calendars: calendars.filter((item) => item.isReadOnly !== true),
    events: [...events, next],
    prefs: data.prefs || {},
  });
}

function timed(day: string, time: string): { start: WirePoint; end: WirePoint } {
  const [year, month, date] = day.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const start = new Date(year, (month || 1) - 1, date || 1, hour || 0, minute || 0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { start: { k: "zoned", v: zoned(start) }, end: { k: "zoned", v: zoned(end) } };
}

function allday(day: string): { start: WirePoint; end: WirePoint } {
  return { start: { k: "plain", v: day }, end: { k: "plain", v: day } };
}

function zoned(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}[Europe/Paris]`;
}
